import { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

/**
 * useUserDoc
 * Small hook that subscribes to a user's Firestore document and returns live data.
 * @param {string|null|undefined} userId - UID to subscribe to; if falsy, uses current user if available.
 * @returns {{ user: any, loading: boolean, error: any }}
 */
export default function useUserDoc(userId) {
    const [state, setState] = useState({ user: null, loading: true, error: null });

    useEffect(() => {
        let authUnsub = null;
        let dataUnsub = null;

        const startListener = (uid) => {
            // Cleanup previous listener before starting new one
            if (dataUnsub) {
                dataUnsub();
                dataUnsub = null;
            }
            
            if (!uid) {
                setState({ user: null, loading: true, error: null });
                return;
            }
            
            const ref = doc(db, 'users', uid);
            dataUnsub = onSnapshot(
                ref,
                (snap) => {
                    if (snap.exists()) {
                        setState({ user: { id: snap.id, ...snap.data() }, loading: false, error: null });
                    } else {
                        setState({ user: null, loading: false, error: null });
                    }
                },
                (err) => {
                    console.error('useUserDoc onSnapshot error', err);
                    setState({ user: null, loading: false, error: err });
                }
            );
        };

        // If caller provides userId, use it directly; otherwise wait for auth
        if (userId) {
            startListener(userId);
        } else {
            // Wait until auth.currentUser is available
            authUnsub = onAuthStateChanged(auth, (u) => {
                if (u && u.uid) {
                    startListener(u.uid);
                } else {
                    setState({ user: null, loading: true, error: null });
                }
            });
        }

        return () => {
            if (authUnsub) authUnsub();
            if (dataUnsub) dataUnsub();
        };
    }, [userId]);

    return state;
}


